'use client';

import { useOrganization } from '@/lib/OrganizationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Building2, LogOut, Mail, Shield } from 'lucide-react';
import { LoadingPage } from '@/components/LoadingPage';
import { InvitationsList } from '@/components/InvitationsList';
import { useSession } from '@/lib/AuthContext';
import { useNavigate } from 'react-router';

export const OrganizationSelection = () => {
  const { organizations, switchOrganization, isLoading } = useOrganization();
  const { signOut } = useSession();
  const navigate = useNavigate();

  const handleSelectOrganization = async (orgId: string) => {
    await switchOrganization(orgId);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleGoBack = async () => {
    await signOut();
    navigate('/login');
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (organizations.length === 0) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-[0.03] [mask-image:linear-gradient(180deg,white,transparent)]" />
        <div
          className="pointer-events-none absolute -left-32 top-0 h-72 w-72 rounded-full opacity-25 blur-3xl"
          style={{ background: 'hsl(var(--primary) / 0.35)' }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-2xl flex-col px-4 py-8 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-6 w-fit gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => void handleGoBack()}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio de sesión
          </Button>

          <div className="flex flex-1 flex-col justify-center space-y-6 pb-8">
            <InvitationsList />

            <Card
              variant="glass"
              className="overflow-hidden border-primary/15 animate-in fade-in slide-in-from-bottom-3 duration-500"
            >
              <div className="h-1 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />

              <CardHeader className="space-y-3 pb-2 text-center sm:text-left">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 sm:mx-0">
                  <Shield className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
                    Acceso restringido
                  </p>
                  <CardTitle className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                    Acceso por invitación
                  </CardTitle>
                  <CardDescription className="mt-2 max-w-md text-muted-foreground/90">
                    Core Utils requiere que una organización te invite antes de entrar al panel.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-5 pt-2">
                <div className="rounded-xl border border-primary/10 bg-primary/[0.04] px-5 py-4">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Si acabas de crear tu cuenta, espera a que un administrador te envíe la
                    invitación por correo. Cuando la aceptes, podrás elegir tu organización aquí.
                  </p>
                </div>

                <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/40 px-4 py-3.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/10">
                    <Mail className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">¿Esperando una invitación?</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      Contacta al administrador de tu organización para solicitar acceso.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 border-t border-primary/10 pt-5 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 gap-2 border-primary/20"
                    onClick={() => void handleGoBack()}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Volver
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleSignOut()}
                    className="flex-1 gap-2 border-red-500/25 bg-red-500/5 text-red-400 hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <LogOut className="h-4 w-4" />
                    Cerrar sesión
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background p-4">
      <div className="pointer-events-none absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-[0.03] [mask-image:linear-gradient(180deg,white,transparent)]" />

      <div className="relative flex min-h-screen items-center justify-center">
        <Card variant="glass" className="w-full max-w-2xl border-primary/15">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl font-bold tracking-tight">
              Selecciona una organización
            </CardTitle>
            <CardDescription className="text-muted-foreground/80">
              Elige una organización para continuar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3">
              {organizations.map((org) => (
                <Card
                  key={org.id}
                  className="cursor-pointer border-primary/10 bg-primary/5 transition-all duration-300 hover:border-primary/30 hover:bg-primary/10"
                  onClick={() => void handleSelectOrganization(org.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {org.logo_url ? (
                        <img 
                          src={org.logo_url} 
                          alt={org.name} 
                          width={40}
                          height={40}
                          loading="lazy"
                          className="h-10 w-10 rounded-lg object-cover" 
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{org.name}</h4>
                        {org.domain && (
                          <p className="text-sm text-muted-foreground/60">{org.domain}</p>
                        )}
                        <p className="text-xs capitalize text-muted-foreground/60">
                          Plan: {org.subscription_plan}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
